import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageSquareText } from "lucide-react";
import {
  NAV_LINKS,
  PHONE_DISPLAY,
  TEL_HREF,
  SMS_HREF,
  LOGO_DARK,
  BUSINESS_NAME,
} from "@/data/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-ink/95 backdrop-blur-md border-b transition-all duration-300 ${
        scrolled
          ? "border-gold/40 shadow-[0_2px_24px_rgba(0,0,0,0.35)]"
          : "border-white/10"
      }`}
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between gap-4">
        <Link
          to="/"
          aria-label={`${BUSINESS_NAME} — home`}
          className="flex items-center shrink-0"
          data-testid="header-logo-link"
        >
          <img
            src={LOGO_DARK}
            alt="Authentically Yours Property Services logo"
            width="240"
            height="240"
            className={`w-auto transition-all duration-300 ${
              scrolled ? "h-20 sm:h-24" : "h-24 sm:h-28"
            }`}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-6" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `link-underline text-[0.86rem] font-medium tracking-wide whitespace-nowrap transition-colors ${
                  isActive ? "text-blush" : "text-white/80 hover:text-white"
                }`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden xl:flex items-center gap-4 shrink-0">
          <a
            href={TEL_HREF}
            className="text-[0.85rem] font-semibold tracking-wide text-white hover:text-blush transition-colors flex items-center gap-2"
            data-testid="header-call-link"
          >
            <Phone size={16} strokeWidth={1.75} aria-hidden="true" />
            {PHONE_DISPLAY}
          </a>
          <Link
            to="/free-estimate"
            className="inline-flex items-center bg-blush text-ink font-semibold text-[0.78rem] tracking-[0.14em] uppercase px-5 py-3 border border-blush hover:bg-rose hover:border-rose hover:text-white transition-colors"
            data-testid="header-estimate-btn"
          >
            Get a Free Estimate
          </Link>
        </div>

        {/* Mobile controls */}
        <div className="flex xl:hidden items-center gap-2">
          <a
            href={TEL_HREF}
            aria-label={`Call ${PHONE_DISPLAY}`}
            className="p-2.5 border border-white/25 text-white"
            data-testid="header-call-mobile"
          >
            <Phone size={18} strokeWidth={1.75} aria-hidden="true" />
          </a>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="p-2.5 border border-white/25 text-white"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — portaled to body so it isn't clipped by the header's backdrop-blur layer */}
      {open &&
        createPortal(
          <div
            className="xl:hidden fixed inset-0 z-[70] bg-black/50"
            onClick={() => setOpen(false)}
            data-testid="mobile-nav-overlay"
          >
          <nav
            className="absolute right-0 top-0 h-full w-[84%] max-w-sm bg-ink shadow-xl px-6 py-6 overflow-y-auto"
            aria-label="Mobile"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end">
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="p-2 border border-white/25 text-white"
                data-testid="mobile-menu-close"
              >
                <X size={20} />
              </button>
            </div>
            <ul className="mt-4 flex flex-col divide-y divide-white/10">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `block py-4 font-serif text-2xl ${
                        isActive ? "text-blush" : "text-white"
                      }`
                    }
                    data-testid={`mobile-nav-${l.label
                      .toLowerCase()
                      .replace(/[^a-z]+/g, "-")}`}
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="mt-6 grid gap-3">
              <Link to="/free-estimate" className="btn-primary btn-on-dark w-full" data-testid="mobile-estimate-btn">
                Get a Free Estimate
              </Link>
              <div className="grid grid-cols-2 gap-3">
                <a href={TEL_HREF} className="btn-ghost btn-on-dark w-full" data-testid="mobile-call-btn">
                  <Phone size={16} strokeWidth={1.75} aria-hidden="true" /> Call
                </a>
                <a href={SMS_HREF} className="btn-ghost btn-on-dark w-full" data-testid="mobile-text-btn">
                  <MessageSquareText size={16} strokeWidth={1.75} aria-hidden="true" /> Text
                </a>
              </div>
            </div>
          </nav>
        </div>,
          document.body
        )}
    </header>
  );
}
